import {
    AccountService,
    AuthService,
    PaymentService,
    UserService,
    AssetService,
} from '@/services';
import {
    getFacebookPages,
    getLang,
    genericRetryStrategy,
    getGa,
    checkOrigin,
    isValueExist,
    filterIdentity,
    getQueryStringParams,
    setLocalStorage,
    setUserToLocalStorage,
    getUserFromLocalStorage,
    getAdrielJwt,
    listIncludes,
    checkConnected,
    noValueExist,
    setAdrielTokens,
    removeAdrielTokens,
} from '@util';
import {
    LAST_ACCOUNT_ID_STORAGE_KEY,
    clearStorageOnLogout,
} from '@/utils/logoutStorage';

import { LOGO_TYPE } from '@/utils/reportMode';

import { defer, empty, from, ReplaySubject } from 'rxjs';

import {
    catchError,
    retryWhen,
    filter,
    distinctUntilChanged,
    shareReplay,
} from 'rxjs/operators';

import {
    CONNECTION_TYPES,
    ACCOUNT_ROLES_NEW,
    MEMBER_STATUS,
    SUBSCRIPTION_STATUS,
    SUBSCRIPTION_PLANS,
    SUBSCRIPTION_STATUS_TYPE,
    DEFAULT_PRICE_PLAN_SETTINGS,
    VIEW_AND_EDIT_ROLE,
    VIEW_AND_EDIT_WITH_MANAGEMENT_ROLE,
    ACCOUNT_ROLE_OPTIONS_NEW,
} from '@consts';
import { stopTracking } from '@/utils/tracker';
import { resolveLegacyRole } from '@/utils/roleResolution';
import ImageService from '../services/ImageService';

const { FB_PAGE, FB_APP, PIXEL, GA, GA4, YOUTUBE_CHANNEL } = CONNECTION_TYPES;

const FB_INFO_FIELDNAME = {
    [FB_PAGE]: 'pages',
    [FB_APP]: 'apps',
    [PIXEL]: 'pixels',
};

const BLOCKED_WS = listIncludes([445461]);

export const _getRoleKey = (role) => {
    return Array.isArray(role) ? role.join('-') : role;
};

function getDefaultState() {
    const dashboardClientCreated$ = new ReplaySubject(1);
    const accountId$ = new ReplaySubject(1);

    let user = getAdrielJwt() && getUserFromLocalStorage();

    user = user ? JSON.parse(user) : null;

    return {
        user,
        account: {},
        // GET /workspace/roles — role 해석(roleId → row)의 데이터 소스.
        // flag(capabilityBasedPermissions) ON일 때만 채워진다. 빈 배열이면
        // resolveLegacyRole이 legacy role 배열로 fallback하므로 로딩 플래그 불필요.
        workspaceRoles: [],
        accountId: 0,
        accounts: undefined,
        authFailed: false,
        fbInfo: {},
        paymentMethods: [],
        referralShown: false,
        gaInfo: {},
        youtubeAssets: [],
        loadingPaymentMethods: false,
        permissionsMissing: false,
        adAccountData: {
            accounts: [],
            code: '',
        },
        authCode: '',
        authError: '',
        boAccessAccount: {},
        provider: '',
        membersToTeam: [],
        localRegister: false,
        userAccountId: 0,
        subscription: undefined,
        dashboardClient: {},
        isUsingUserAccount: false,
        isExceededNumberOfConnections: false,
        accountId$,
        accountFetched$: accountId$.pipe(
            filter(R.identity),
            distinctUntilChanged(),
            shareReplay(1)
        ),
        dashboardClientCreated$,
        /**
         * Pending login challenge after `mfa_required`: `{ token, methods,
         * expiresAt, serverExpiresAt, authSource, continuation, returnTo }`.
         * `expiresAt` is on this device's clock (see startMfaChallenge). Memory only — the
         * mfa_token must never be persisted, so a reload drops it.
         */
        mfaChallenge: null,
        /** How the current session passed MFA ('totp' | 'backup_code'). */
        mfaLoginMethod: null,
    };
}

// function getHigherRole(role1, role2) {
//     const roles = [role1, role2];
//     const { ADMIN, ONLY_VIEW, VIEW_AND_EDIT } = ACCOUNT_ROLES_NEW;

//     if (roles.includes(ADMIN)) return ADMIN;
//     if (roles.includes(VIEW_AND_EDIT)) return VIEW_AND_EDIT;
//     return ONLY_VIEW;
// }

const mapTeamToMember = (membersToTeam = []) => {
    const _sharingMembers = {};
    // team
    membersToTeam.forEach(({ id, name, members = [] }) => {
        // member
        members.forEach(({ invitation_id }) => {
            _sharingMembers[invitation_id] = {
                name,
                id,
            };
        });
    });
    return _sharingMembers;
};

/**
 * 멤버 행(sharingMembers) 표시 전용으로만 남은 legacy 정규화.
 * 내 role 판정(user/role getter)은 utils/roleResolution으로 옮겨갔다 — 특히
 * 아래의 "빈 배열 → ['admin']" 승격은 거기서 backoffice 분기로 대체됐고,
 * 멤버 행은 role이 비지 않으므로 이 기본값에 닿지 않는다.
 */
const getRefinedRollByAgentUsage = ({ role, isAgency }) => {
    const roleWithDefault =
        role?.length === 0 ? ['admin'] : role ?? ['view_only'];

    const typeCheckedRole = R.is(Array, roleWithDefault)
        ? roleWithDefault
        : [roleWithDefault];

    if (!isAgency) {
        const islimitedViewAndEdit =
            typeCheckedRole.includes(ACCOUNT_ROLES_NEW.VIEW_AND_EDIT) &&
            !typeCheckedRole.includes(ACCOUNT_ROLES_NEW.CONNECTION_MANAGEMENT);

        if (islimitedViewAndEdit) {
            return [
                ...typeCheckedRole,
                ACCOUNT_ROLES_NEW.CONNECTION_MANAGEMENT,
            ];
        }
    }

    return typeCheckedRole;
};

export default {
    namespaced: true,
    state: getDefaultState(),
    mutations: {
        login(state, user) {
            if (!user) {
                return;
            }

            if (R.equals(user, state.user)) {
                return;
            }

            if (!user.backoffice_admin && isValueExist(user)) {
                const {
                    token,
                    refresh_token,
                    expiration_time_token_seconds,
                    expiration_time_refresh_seconds,
                    ...userWithoutToken
                } = user;

                setAdrielTokens({
                    token,
                    refresh_token,
                    expiration_time_token_seconds,
                    expiration_time_refresh_seconds,
                });
                AuthService.setAccessToken$.next(token);

                setUserToLocalStorage(userWithoutToken);
            }

            state.user = Object.freeze(user);
        },
        authFail(state, value = true) {
            state.authFailed = value;
        },
        logout(state) {
            state.user = null;
            state.account = {};
            state.accountId = 0;
            state.accounts = [];
            state.mfaChallenge = null;
            state.mfaLoginMethod = null;
        },
        setMfaChallenge(state, challenge) {
            state.mfaChallenge = challenge;
        },
        clearMfaChallenge(state) {
            state.mfaChallenge = null;
        },
        setMfaLoginMethod(state, method) {
            state.mfaLoginMethod = method;
        },
        register(state) {
            state.localRegister = true;
        },
        setFbInfo(state, info) {
            state.fbInfo = info;
        },
        setAdAccountData(state, data) {
            state.adAccountData = data;
        },
        setAccounts(state, accounts) {
            state.accounts = Object.freeze(accounts);
        },
        setYouTubeAssets(state, youtubeAssets) {
            state.youtubeAssets = youtubeAssets;
        },
        setPaymentMethods(state, paymentMethods) {
            state.paymentMethods = paymentMethods;
        },
        setReferralShown(state, value) {
            state.referralShown = value;
        },
        setGaInfo(state, value) {
            state.gaInfo = value;
        },
        clearUser(state) {
            Object.assign(state, getDefaultState());
        },
        setPixelInfo(state, data) {
            state.fbInfo = data;
        },
        setPermissionsMissing(state, value) {
            state.permissionsMissing = value;
        },
        setAccountId(state, id) {
            const userAccountId = getQueryStringParams('userAccountId');
            const isAdmin = R.path(['user', 'backoffice_admin'], state);

            if (id == state.accountId) {
                return;
            }

            state.isUsingUserAccount = false;

            if (isAdmin && userAccountId) {
                if (id != userAccountId) {
                    id = userAccountId;
                    state.isUsingUserAccount = true;
                }
            }
            axios.defaults.headers.common.AccountId = id;
            state.accountId = id;

            state.accountId$.next(id);
            setLocalStorage(LAST_ACCOUNT_ID_STORAGE_KEY, id);
        },
        setAuthCode(state, code) {
            state.authCode = code;
        },
        setAuthError(state, error) {
            state.authError = error;
        },
        deleteAccount(state, accountId) {
            state.accounts = R.reject(
                R.propSatisfies((id) => id == accountId, 'id'),
                state.accounts || []
            );
        },
        setMembersToTeam(state, value = []) {
            state.membersToTeam = value;
        },
        setBoAccessAccount(state, account = {}) {
            state.boAccessAccount = account;
        },
        setAccount(state, account = {}) {
            state.account = Object.freeze(account);
        },
        setWorkspaceRoles(state, roles = []) {
            state.workspaceRoles = Object.freeze(roles);
        },
        updateWorkspace(state, { name, teams = [] }) {
            state.workspaceName = name;
            state.teams = teams;
        },
        setUserAccountId(state, value) {
            state.userAccountId = Number(value);
        },
        setSubscription(state, value) {
            state.subscription = Object.freeze(value);
        },
        setDashboardClient(state, dashboardClient) {
            state.dashboardClient = dashboardClient;
            state.dashboardClientCreated$.next(dashboardClient);
        },
        setIsExceededNumberOfConnections(state, value) {
            state.isExceededNumberOfConnections = value;
        },
    },
    actions: {
        async checkSession({ commit }, ignoreFail = false) {
            return AuthService.session()
                .then((user) => {
                    commit('authFail', false);
                    commit('login', user);

                    if (user.backoffice_admin) {
                        stopTracking();
                    }

                    const { email = '' } = user;

                    if (email) {
                        var _hsq = (window._hsq = window._hsq || []);
                        _hsq.push([
                            'identify',
                            {
                                email,
                            },
                        ]);
                    }

                    return user;
                })
                .catch((error) => {
                    ignoreFail !== true && commit('authFail', true);
                    throw new Error(error);
                });
        },
        login({ commit }, user) {
            commit('login', user);
        },
        logout({ commit }) {
            // Client-side cleanup must run whether or not the /logout request
            // succeeds — otherwise a failed/expired-session logout leaves PII
            // and tokens in storage while the user is sent to the login page.
            const clearClientState = () => {
                removeAdrielTokens();
                clearStorageOnLogout();
                commit('clearUser');
                commit('newDashboard/setViews', [], { root: true });
                commit('newDashboard/setOriginViews', {}, { root: true });
            };
            return AuthService.logout().finally(clearClientState);
        },
        fetchFacebookProfile({ commit, state }, token) {
            let authType;
            if (state.permissionsMissing) authType = 'rerequest';

            return new Promise((resolve, reject) => {
                from(getFacebookPages({ token, authType })).subscribe(
                    (data) => {
                        commit('setPermissionsMissing', false);
                        commit('setFbInfo', data);
                        resolve();
                    },
                    (err = { code: 'UNKNOWN_ERROR' }) => {
                        if (err.code === 'PERMISSIONS_MISSING') {
                            commit('setPermissionsMissing', true);
                            reject();
                        }
                    }
                );
            });
        },
        fetchPaymentMethods({ commit }) {
            const api = PaymentService.getPaymentMethodsV2;

            return api().then((paymentMethods) =>
                commit('setPaymentMethods', paymentMethods)
            );
        },
        fetchAccountList({ commit }) {
            return new Promise((resolve, reject) => {
                return defer(() => AccountService.list())
                    .pipe(
                        retryWhen(genericRetryStrategy()),
                        catchError(R.always(empty()))
                    )
                    .subscribe(
                        (accounts) => {
                            // set default at first item
                            const getIsDefault = R.propEq('is_default', true);
                            const _accounts = [
                                R.find(getIsDefault, accounts),
                                ...R.reject(getIsDefault, accounts),
                            ].filter(R.identity);

                            commit('setAccounts', _accounts);
                            resolve(accounts);
                        },
                        (err) => {
                            reject(err);
                            console.log(err);
                        }
                    );
            });
        },
        updateBusinessNumber(
            { commit, getters: { user }, dispatch },
            business_no
        ) {
            commit('login', { ...user, business_no });
            return UserService.updateBizNo(business_no).then(() => {
                return dispatch('checkSession');
            });
        },
        updateBusinessNumberByAccount(
            { getters: { accountId }, dispatch },
            bizNo
        ) {
            return AccountService.setBusinessNoPerAccount({
                id: accountId,
                reqBody: { bizNo },
            }).then(() => {
                return dispatch('fetchWorkspace', { with_biz_files: true });
            });
        },
        createAccount({ dispatch, commit }, { account, shouldSetId }) {
            return UserService.createAccount(account).then((account) => {
                if (shouldSetId) {
                    commit('setAccountId', account.id);
                }

                dispatch('fetchWorkspace');
                return account;
            });
        },
        deleteWorkspace(
            { commit, getters: { defaultAccountId }, dispatch },
            id
        ) {
            return AccountService.deleteWorkspace(id).then(() => {
                commit('deleteAccount', id);
                commit('setAccountId', defaultAccountId);
                return dispatch('fetchWorkspace');
            });
        },
        setDefaultAccount({ dispatch }, id) {
            UserService.setDefaultAccount(id).then(() => {
                dispatch('fetchAccountList');
            });
        },
        getUserData({ commit }) {
            UserService.authGet().then((user) => {
                commit('login', user);
            });
        },
        updateKakaoNoti({ dispatch }, value) {
            return UserService.setKakaoNoti(value).then(() => {
                return dispatch('getUserData');
            });
        },
        fetchGaInfo({ commit }) {
            return getGa()
                .then((data) => commit('setGaInfo', data))
                .catch((err = {}) => {
                    console.log(err);
                    if (err.status === 403) {
                        commit('setGaInfo');
                    } else if (err.error === 'popup_closed_by_user') {
                        throw new Error(err);
                    }
                });
        },
        fetchAssetThroughLink({ commit, state }, { origin, isTrusted, data }) {
            return new Promise((resolve, reject) => {
                if (!checkOrigin(origin)) {
                    reject('not matched origin');
                    return;
                }

                if (isTrusted && isValueExist(data)) {
                    let { type, code } = data;

                    if (!code) {
                        reject('no code');
                        return;
                    }

                    commit('setAuthCode', code);

                    AssetService.getAssets(code)
                        .then(({ assets = [] }) => {
                            if (state.authError) commit('setAuthError', '');
                            assets = filterIdentity(assets);
                            switch (type) {
                                case FB_PAGE:
                                case FB_APP:
                                case PIXEL:
                                    console.log(
                                        'fetchAssetThroughLink',
                                        assets
                                    );
                                    commit('setFbInfo', {
                                        ...state.fbInfo,
                                        [FB_INFO_FIELDNAME[type]]: assets,
                                    });
                                    commit('setPermissionsMissing', false);
                                    break;
                                case YOUTUBE_CHANNEL:
                                    commit('setYouTubeAssets', assets);
                                    break;
                                case GA:
                                case GA4:
                                    commit('setGaInfo', {
                                        ...state.gaInfo,
                                        properties: assets,
                                    });
                                    break;
                                default:
                                    commit('setAdAccountData', {
                                        accounts: assets,
                                        code: code,
                                    });
                                    break;
                            }
                            resolve(assets);
                        })
                        .catch((err) => {
                            console.error(err);
                            //provisionally till this issue fixed
                            const { code = '' } = R.pathOr(
                                {},
                                ['response', 'data'],
                                err
                            );
                            if (code === 'ERR_QUOTA_EXCEEDED') {
                                commit('setAuthError', code);
                            }
                            reject(err);
                        });
                }
            });
        },
        getBoAccessAccount({ commit }, accountId) {
            return AccountService.getAccount(accountId)
                .then((data) => commit('setBoAccessAccount', data))
                .catch(console.error);
        },
        setDefaultCard({ dispatch }, cardId) {
            const api = PaymentService.makeDefaultV2;

            return api(cardId).then(() => dispatch('fetchPaymentMethods'));
        },
        fetchWorkspace({ commit, dispatch }, { with_biz_files } = {}) {
            return AccountService.getWorkspace({
                with_biz_files,
            }).then((data) => {
                let accountId = R.propOr(0, 'id', data);

                commit('setAccount', data);
                commit('setAccountId', accountId);

                // role 해석(roleId → row)의 데이터 소스. flag OFF 경로는 roles를
                // 아예 참조하지 않으므로 fetch를 생략하고 비워둔다.
                if (R.path(['meta', 'capabilityBasedPermissions'], data)) {
                    dispatch('fetchWorkspaceRoles');
                } else {
                    commit('setWorkspaceRoles', []);
                }

                return data;
            });
        },
        fetchWorkspaceRoles({ commit }) {
            return AccountService.getWorkspaceRoles()
                .then((roles) => commit('setWorkspaceRoles', roles))
                // 실패해도 치명적이지 않다 — resolveLegacyRole이 legacy role로 fallback.
                .catch(console.error);
        },
        updateWorkspace({ commit, state, dispatch }, { name, teams }) {
            return AccountService.updateV2({ name, teams }).then(() => {
                // update {workspace name} in workspace, workspace list
                const { accountId, account, accounts } = state;
                const accountsList = accounts || [];

                const updatedWorkspace = R.assocPath(['name'], name, account);
                commit('setAccount', updatedWorkspace);

                const idx = R.findIndex(
                    R.propEq('id', accountId),
                    accountsList
                );
                if (idx !== -1) {
                    const updatedWorkspaces = R.assocPath(
                        [idx, 'name'],
                        name,
                        accountsList
                    );
                    commit('setAccounts', updatedWorkspaces);
                }

                return dispatch('fetchMembers');
            });
        },
        fetchMembers({ commit }) {
            return AccountService.fetchMembersV2().then((data = []) => {
                commit('setMembersToTeam', data);
                return data;
            });
        },
        deleteMember({ commit, state }, req) {
            return AccountService.deleteMemberV2(req).then(() => {
                const user_id = R.pathOr('', ['role', 'user_id'], req);
                const removeMember = R.reject(R.propEq('user_id', user_id));

                const updatedMembers = state.membersToTeam.map((team) => {
                    const { members } = team;

                    return { ...team, members: removeMember(members) };
                });

                commit('setMembersToTeam', updatedMembers);
            });
        },
        deletePendings({ commit, state }, { invitationIds }) {
            invitationIds.forEach((invitation_id) => {
                AccountService.deletePendingUser({ invitation_id });
            });

            const removeIds = R.compose(
                R.reject(
                    R.propSatisfies(
                        R.includes(R.__, invitationIds),
                        'invitation_id'
                    )
                ),
                R.prop('members')
            );

            const updatedMembers = R.map(
                (team) => R.assoc('members', removeIds(team), team),
                state.membersToTeam
            );

            commit('setMembersToTeam', updatedMembers);
        },
        getSubscription({ commit, state }) {
            const { accountId, userAccountId } = state;
            PaymentService.getSubscriptionV2({
                userAccountId: userAccountId || accountId,
            }).then((v) => {
                commit('setSubscription', v);
            });
        },
        updateCurrency({ commit, state }, { currency }) {
            return AccountService.updateCurrencyV2({ currency }).then(() => {
                commit(
                    'setAccount',
                    R.assoc('currency', currency, state.account)
                );
            });
        },
        updateWorkspaceColorUrl(context, { color, websiteUrl }) {
            const { commit, state } = context;

            return AccountService.updateWorkspaceColorUrl({
                color,
                websiteUrl,
            }).then(() => {
                const updatedWorkspace = R.compose(
                    R.assoc('color', color),
                    R.assocPath(['meta', 'websiteUrl'], websiteUrl)
                )(state.account);
                commit('setAccount', updatedWorkspace);
                commit(
                    'setAccounts',
                    R.map(
                        R.when(
                            R.propEq('id', state.accountId),
                            R.assoc('color', color)
                        ),
                        state.accounts || []
                    )
                );
            });
        },
        deleteBrandingLogoImage({ commit, state }) {
            return ImageService.deleteBrandingLogo({ type: 'logo' }).then(
                () => {
                    commit(
                        'setAccount',
                        R.assocPath(['logo_media'], undefined)(state.account)
                    );
                }
            );
        },
        updateLogoImage({ commit, state, rootGetters }, payload) {
            // type: 0 agency logo, 1 client logo
            const {
                logo,
                file,
                viewId,
                logoType = LOGO_TYPE.WORKSPACE,
                accountId,
            } = payload;

            // logo upload common info
            const formData = new FormData();
            formData.append('files', file);
            // formData.append('accountId', workspaceId);
            formData.append('logoName', '');

            // client logo upload info
            if (LOGO_TYPE.VIEW === logoType) {
                formData.append('view_id', viewId);
                formData.append('workspace_id', accountId);
                // console.log(file, viewId, logoType, accountId);
            }

            // TODO need to
            return ImageService.uploadMyFile({
                type: 'logo',
                formData,
                params: {
                    isPublic: 1,
                },
            }).then(({ data }) => {
                const { path } = data;
                let newView = R.prop('newDashboard/currentView')(rootGetters);

                switch (logoType) {
                    case LOGO_TYPE.WORKSPACE:
                        commit(
                            'setAccount',
                            R.assocPath(
                                ['logo_media', 'source_path'],
                                path
                            )(state.account)
                        );
                        return logo;

                    case LOGO_TYPE.VIEW:
                        newView = R.assocPath(
                            ['logoMedia', 'source_path'],
                            path
                        )(newView);

                        commit('newDashboard/updateView', newView, {
                            root: true,
                        });

                        break;
                    default:
                }
            });
        },
    },

    getters: {
        /**
         * Method-style on purpose: a plain getter is cached until
         * `mfaChallenge` changes, so it would keep answering `true` after
         * `expiresAt` passes. Call it: `getters['user/isMfaChallengeLive']()`.
         */
        isMfaChallengeLive:
            ({ mfaChallenge }) =>
            (now = Date.now()) =>
                !!mfaChallenge?.token &&
                Date.parse(mfaChallenge.expiresAt) > now,
        isLimitedUser(_, getters) {
            const { pricePlanSettings } = getters;

            if (isValueExist(pricePlanSettings)) {
                const withoutAllowAllSettings = R.omit(
                    [
                        'allowAllConnectors',
                        'allowAllWidgetTypes',
                        'allowDataSourceSettings',
                    ],
                    pricePlanSettings
                );

                if (isValueExist(withoutAllowAllSettings)) {
                    return true;
                } else {
                    const isAllowAllSetAsTrue = R.whereEq({
                        allowAllConnectors: true,
                        allowAllWidgetTypes: true,
                        allowDataSourceSettings: true,
                    })(pricePlanSettings);

                    return !isAllowAllSetAsTrue;
                }
            } else {
                return false;
            }
        },
        brandingWebsiteUrl: R.path(['account', 'meta', 'websiteUrl']),
        brandingLogoSourcePath: (_, { logoMedia }) => {
            return R.path(['source_path'])(logoMedia) || undefined;
        },
        hasAgencyLogo: (_, { logoMedia }) =>
            isValueExist(R.path(['source_path'])(logoMedia)),
        logoMedia: R.path(['account', 'logo_media']),
        // meta: R.path(['account', 'meta']),
        user: R.prop('user'),
        accountId: R.pathOr(0, ['account', 'id']),
        accounts: R.prop('accounts'),
        account: R.prop('account'),
        authFailed: R.prop('authFailed'),
        pricePlanSettings: R.compose(
            R.when(isValueExist, R.mergeRight(DEFAULT_PRICE_PLAN_SETTINGS)),
            R.pathOr({}, ['account', 'price_plan_settings'])
        ),
        hasOnlyAvailableConnectors(_, getters) {
            const { pricePlanSettings, isLimitedUser } = getters;

            const {
                availableConnectorTypes = [],
                allowAllConnectors = false,
                allowAllConnectorTypes,
            } = pricePlanSettings;

            const hasLimittedConnectorSetting =
                availableConnectorTypes.length > 0 || !allowAllConnectors;

            //  availableConnectorTypes = undefined means, not limited
            //  availableConnectorTypes = [] empty array means, not limited
            if (
                isLimitedUser &&
                hasLimittedConnectorSetting &&
                !allowAllConnectorTypes
            ) {
                return R.compose(
                    Boolean,
                    R.length,
                    R.pathOr([], ['availableConnectorTypes'])
                )(pricePlanSettings);
            } else {
                return false;
            }
        },
        hasOnlyAvailableWidgets(_, getters) {
            const { pricePlanSettings, isLimitedUser } = getters;

            const { availableWidgetTypes = [], allowAllWidgetTypes = false } =
                R.pick(
                    ['availableWidgetTypes', 'allowAllWidgetTypes'],
                    pricePlanSettings
                );

            const hasLimittedWidgetSetting =
                availableWidgetTypes.length > 0 || !allowAllWidgetTypes;

            //  availableWidgetTypes = undefined means, not limited
            //  availableWidgetTypes = [] empty array means, not limited
            if (isLimitedUser && hasLimittedWidgetSetting) {
                return R.compose(
                    Boolean,
                    R.length,
                    R.pathOr([], ['availableWidgetTypes'])
                )(pricePlanSettings);
            } else {
                return false;
            }
        },
        isOverLimitedViews(_, getters, rootState) {
            const { pricePlanSettings = {}, isLimitedUser } = getters;

            const views = R.pathOr([], ['newDashboard', 'views'], rootState);

            const nbOfViews = pricePlanSettings.nbOfViews || -1;

            return isLimitedUser && nbOfViews > -1
                ? R.compose(
                      R.gte(R.__, nbOfViews),
                      R.length,
                      R.flatten,
                      R.map(R.propOr([{}], 'children'))
                  )(views)
                : false;
        },
        credit: R.compose(
            R.pathOr('0', ['account', 'credits', 'credits']),
            R.nthArg(1)
        ),
        deposit: R.compose(
            R.pathOr({}, ['account', 'deposits', 0]),
            R.nthArg(1)
        ),
        vbank: R.compose(R.pathOr({}, ['account', 'vbank']), R.nthArg(1)),
        fbInfo: R.prop('fbInfo'),
        gaInfo: R.compose(R.defaultTo({}), R.prop('gaInfo')),
        referralShown: R.prop('referralShown'),
        paymentMethods: R.prop('paymentMethods'),
        defaultPaymentMethod: R.compose(
            R.find(R.prop('default')),
            R.prop('paymentMethods')
        ),
        hasPaymentMethod(state) {
            return state.paymentMethods.length > 0;
        },
        isAdmin: R.path(['user', 'backoffice_admin']),
        userLang: R.compose(getLang, R.prop('user')),
        defaultAccount: R.compose(
            R.defaultTo({}),
            R.find(R.prop('is_default')),
            R.propOr([], 'accounts'),
            R.nthArg(1)
        ),
        defaultAccountId: R.compose(
            R.pathOr('', ['defaultAccount', 'id']),
            R.nthArg(1)
        ),
        isDefault: (_, { accountId, defaultAccountId }) => {
            return accountId === defaultAccountId;
        },
        userId: R.path(['user', 'id']),
        accountToken: R.compose(
            R.pathOr('', ['account', 'token']),
            R.nthArg(1)
        ),
        smartOnboardingEnalbed(_, { userId }) {
            return !!(Number(userId) % 2);
        },
        isKoUser: R.pathEq(['user', 'language'], 'ko'),
        isJPUser: R.pathEq(['user', 'language'], 'ja'),
        isKrUser: R.pathEq(['user', 'country'], 'KR'),
        email: R.path(['user', 'email']),
        planType(_, { account }) {
            return account.account_type;
        },
        /**
         * 내 role의 판정용 legacy 배열 — permission.js의 2층(role 판정)이 읽는
         * 단일 소스. 해석 규칙은 utils/roleResolution(resolveLegacyRole):
         * flag ON이면 roleId → row(custom은 view_only 확정), 아니면 legacy role,
         * 둘 다 비면 backoffice admin 여부로 가른다.
         */
        role: ({ workspaceRoles }, { account, isAgency, isAdmin }) => {
            return resolveLegacyRole({
                capabilityBasedPermissions: Boolean(
                    account?.meta?.capabilityBasedPermissions
                ),
                roleId: account?.roleId,
                role: account?.role,
                roles: workspaceRoles,
                isAgency: Boolean(isAgency),
                isBackofficeAdmin: Boolean(isAdmin),
            });
        },
        isKrwUser(_, { account }) {
            return account.currency === 'KRW';
        },
        accountCurrency({ boAccessAccount }, { account }) {
            return boAccessAccount.currency || account.currency || 'USD';
        },
        membersToTeam: R.prop('membersToTeam'),
        // exclude 'no team', which is { name: null }
        teams: R.compose(
            R.filter(R.prop('name')),
            R.project(['id', 'name']),
            R.propOr([], 'membersToTeam')
        ),
        members: R.compose(
            R.flatten,
            R.map(R.prop('members')),
            R.propOr([], 'membersToTeam')
        ),
        teamsAndRoles: (_, { user = {}, sharingMembers }) => {
            user = user || {};
            const myTeamInfo = R.find(
                R.propEq('user_id', user.id),
                sharingMembers
            );
            return R.propOr([], 'teamsAndRoles', myTeamInfo);
        },
        sharingMembers: (state, getters) => {
            const { members, isAgency } = getters;
            const { membersToTeam } = state;
            const _getEmail = ({ user, target_email }) =>
                R.propOr(target_email || '', 'email', user);
            const _getKey = ({ user_id, target_email, status }) =>
                status === MEMBER_STATUS.CONNECTED ? user_id : target_email;

            const _bindedMembers = {};
            const _teamToMember = mapTeamToMember(membersToTeam);

            members.forEach((member) => {
                const { user, role, user_id, invitation_id } = member;

                const refinedRoll = getRefinedRollByAgentUsage({
                    role,
                    isAgency,
                });

                const email = _getEmail(member);
                const memberKey = _getKey(member);

                const {
                    // highestRole,
                    userIds,
                    teams,
                    teamsAndRoles,
                    invitationIds,
                } = R.propOr({}, memberKey, _bindedMembers);

                const { id, name } = _teamToMember[invitation_id];

                _bindedMembers[memberKey] = {
                    ...member,
                    ..._bindedMembers[memberKey],
                    role: refinedRoll,
                    name: R.propOr('', 'name', user),
                    // highestRole: getHigherRole(role, highestRole),
                    userIds: R.append(user_id, userIds),
                    teams: name ? R.append(name, teams) : teams,
                    teamsAndRoles: R.append(
                        { id, name, role: refinedRoll },
                        teamsAndRoles
                    ),
                    invitationIds: R.append(invitation_id, invitationIds),
                    email,
                };
            });

            return Object.values(_bindedMembers);
        },
        connectedSharingMembers: (_, getters) => {
            const { sharingMembers } = getters;
            return sharingMembers.filter(checkConnected);
        },
        noTeamMemberIds: (_, getters) => {
            const { sharingMembers } = getters;
            return R.compose(
                R.pluck('user_id'),
                R.filter(R.propSatisfies(noValueExist, 'teams'))
            )(sharingMembers);
        },
        subscriptionStatus(
            { subscription, accountId },
            _,
            rootState,
            rootGetters
        ) {
            const isAdmin = rootGetters['user/isAdmin'];
            const { AVAILABLE, ENDED } = SUBSCRIPTION_STATUS;
            const { ACTIVE, TRIALING } = SUBSCRIPTION_STATUS_TYPE;

            if (isAdmin || !isValueExist(subscription)) {
                return AVAILABLE;
            }

            let { status } = subscription || {};

            if (BLOCKED_WS(Number(accountId))) {
                return ENDED;
            }

            return listIncludes([ACTIVE, TRIALING], status) ? AVAILABLE : ENDED;
        },
        subscriptionStatusType: R.compose(
            R.pathOr(SUBSCRIPTION_STATUS_TYPE.CANCELED, [
                'subscription',
                'status',
            ])
        ),
        isSubscriptionTrial(_, { subscriptionStatusType, subscriptionPlan }) {
            return (
                subscriptionPlan === SUBSCRIPTION_PLANS.BASIC ||
                subscriptionStatusType === SUBSCRIPTION_STATUS_TYPE.TRIALING
            );
        },
        dashboardClient: R.prop('dashboardClient'),
        userAccountId: R.prop('userAccountId'),
        isAdminUsingUserAccount(state, getters) {
            return state.isUsingUserAccount && getters.isAdmin;
        },
        paymentType: R.path(['account', 'payment_type']),
        isAppsumo: R.compose(R.propEq('paymentType', 'app_sumo'), R.nthArg(1)),
        subscriptionPlan: R.pathOr(SUBSCRIPTION_PLANS.BASIC, [
            'subscription',
            'plan',
        ]),
        isWadizStore: R.path(['account', 'meta', 'wadiz_store']),
        isWadizInbound: R.path(['account', 'meta', 'wadiz_inbound']),
        isWadizStoreByAdmin(_, { isWadizStore, isAdmin }) {
            return isWadizStore && isAdmin;
        },
        isSocialUser: R.pathSatisfies(Boolean, ['user', 'provider']),
        isSubscriptionTrialEnded(
            _,
            { subscriptionStatus, isSubscriptionTrial }
        ) {
            return (
                subscriptionStatus === SUBSCRIPTION_STATUS.ENDED &&
                isSubscriptionTrial
            );
        },
        isSubscriptionPaid(_, getters) {
            return !getters['isSubscriptionTrialEnded'];
        },
        isWadiz(_, { isWadizStore, isWadizInbound }) {
            return isWadizStore || isWadizInbound;
        },
        canViewOnlyUsersEditReportModeOnThisAccount: (state, { accountId }) => {
            // now permission to edit print cover => TODO
            let flag = false;
            const listToAllow = [];

            const wadizAccountIds = [28905, 28911, 465881];

            listToAllow.push(...wadizAccountIds);
            flag = R.includes(accountId, listToAllow);

            return flag;
        },
        shouldShowReportMode: (state, { accountId, email }) => {
            let flag = true;

            const accountListToBlock = [];
            const emailListToBlock = [];

            // const wadizEmailEndsWith = 'wadiz.kr';
            // const wadizAccountIds = [28911, 146681, 28905, 135301];

            // accountListToBlock.push(...wadizAccountIds);
            // emailListToBlock.push(wadizEmailEndsWith);

            flag =
                !R.any(R.includes(R.__, email))(emailListToBlock) &&
                !R.includes(accountId)(accountListToBlock);

            return flag;
        },
        isNicePg(_, { paymentType }) {
            return paymentType === 'nice';
        },
        isLookerStudio: R.pathOr(false, ['account', 'meta', 'lookerStudio']),
        subscriptionPaymentType: R.path(['subscription', 'paymentType']),
        // Custom exchange rate button gate (workspace + teamspace).
        // Per TASK-13090 the two backoffice options — the price-plan entitlement
        // (allowCurrencyByFolder) and the custom exchange rate feature flag
        // (meta.customExchangeRate) — are OR-combined so either one shows the
        // custom-rate buttons; both must be off to hide them.
        // The folder currency dropdown is separate and keeps its enable on
        // allowCurrencyByFolder (blocked when off). meta.multiCurrency is excluded —
        // it is now always treated as on, so it carries no gating signal.
        customExchangeRateEnabled(_, getters) {
            const { pricePlanSettings, account } = getters;
            const meta = R.propOr({}, 'meta', account);

            return Boolean(
                R.propOr(false, 'allowCurrencyByFolder', pricePlanSettings) ||
                    meta.customExchangeRate
            );
        },
        isAutoCustomBreakdownCascadeLevelEnabled: R.pathOr(false, [
            'account',
            'meta',
            'autoCustomBreakdownCascadeLevel',
        ]),
        isQa: R.pathOr(false, ['user', 'meta', 'isQa']),
        isExceededNumberOfConnections: R.prop('isExceededNumberOfConnections'),
        isSubscriptionCancellable: R.pathOr(false, [
            'account',
            'meta',
            'subscriptionCancellable',
        ]),
        isAgency: R.pathOr(false, ['account', 'meta', 'isAgency']),
        fieldV3Account: R.pathOr(false, [
            'account',
            'meta',
            'useAvalaibleFieldsV3',
        ]),
        accountRoleOptions: (_, getters) => {
            const { isAgency } = getters;

            if (isAgency) {
                return R.insert(
                    1,
                    VIEW_AND_EDIT_ROLE,
                    ACCOUNT_ROLE_OPTIONS_NEW
                ).map((option) => {
                    if (
                        R.equals(
                            option.value,
                            VIEW_AND_EDIT_WITH_MANAGEMENT_ROLE.value
                        )
                    ) {
                        return {
                            ...option,
                            label: 'MULTI_ACCOUNT.role_view_and_edit_with_management',
                        };
                    }

                    return option;
                });
            }

            return ACCOUNT_ROLE_OPTIONS_NEW;
        },
    },
};
